/**
 * Location Service
 * 
 * Handles geolocation, distance calculations, and provider sorting by proximity.
 */

// ─── GET USER LOCATION ─────────────────────────────────────────────────────

/**
 * Get the user's current GPS coordinates using the browser Geolocation API.
 * Returns { latitude, longitude } or throws an error.
 */
export function getUserLocation() {
    return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            reject(new Error('Geolocation is not supported by your browser.'));
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                resolve({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                });
            },
            (error) => {
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        reject(new Error('Location access denied. Please enable location permissions.'));
                        break;
                    case error.POSITION_UNAVAILABLE:
                        reject(new Error('Location information unavailable.'));
                        break;
                    case error.TIMEOUT:
                        reject(new Error('Location request timed out.'));
                        break;
                    default:
                        reject(new Error('Unable to get your location.'));
                }
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0, // Always get fresh location
            }
        );
    });
}

// ─── DISTANCE CALC (Haversine) ──────────────────────────────────────────────

/**
 * Calculate distance between two lat/lng points in km using the Haversine formula.
 */
export function getDistanceKm(lat1, lon1, lat2, lon2) {
    const R = 6371; // Earth's radius in km
    const toRad = (deg) => (deg * Math.PI) / 180;

    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);

    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

/**
 * Format distance for display.
 */
export function formatDistance(km) {
    if (km < 1) return `${Math.round(km * 1000)} m`;
    if (km < 10) return `${km.toFixed(1)} km`;
    return `${Math.round(km)} km`;
}

// ─── SORT PROVIDERS BY DISTANCE ─────────────────────────────────────────────

/**
 * Sort providers by distance from a given point.
 * Providers without coordinates are pushed to the end.
 * 
 * @param {Array} providers - Array of provider objects (must have latitude/longitude fields)
 * @param {number} userLat - User's latitude
 * @param {number} userLng - User's longitude
 * @returns {Array} Providers sorted by distance, each with a `distance` field (in km)
 */
export function sortProvidersByDistance(providers, userLat, userLng) {
    return providers
        .map((provider) => {
            if (provider.latitude && provider.longitude) {
                const distance = getDistanceKm(userLat, userLng, provider.latitude, provider.longitude);
                return { ...provider, distance };
            }
            return { ...provider, distance: Infinity };
        })
        .sort((a, b) => a.distance - b.distance);
}

/**
 * Filter providers within a radius (km).
 */
export function filterProvidersByRadius(providers, userLat, userLng, radiusKm) {
    return sortProvidersByDistance(providers, userLat, userLng)
        .filter((p) => p.distance <= radiusKm);
}

// ─── URGENCY MODES ──────────────────────────────────────────────────────────

/**
 * IMMEDIATE: Nearest available providers, sorted by distance (smallest first).
 * FLEXIBLE:  Providers filtered to user's area (~25 km), sorted by rating.
 */
export const URGENCY_MODES = {
    IMMEDIATE: 'immediate',
    FLEXIBLE: 'flexible',
};

/**
 * Get providers based on urgency mode.
 * 
 * @param {Array} providers - All providers for a service category
 * @param {number} userLat - User's latitude
 * @param {number} userLng - User's longitude
 * @param {string} mode - 'immediate' or 'flexible'
 * @returns {Array} Sorted/filtered providers with distance field
 */
export function getProvidersByUrgency(providers, userLat, userLng, mode) {
    if (mode === URGENCY_MODES.IMMEDIATE) {
        // Nearest first, include all (even far ones if nothing is close)
        const sorted = sortProvidersByDistance(providers, userLat, userLng);
        // Only include providers that have location data, then append the rest
        const withLocation = sorted.filter(p => p.distance !== Infinity);
        const withoutLocation = sorted.filter(p => p.distance === Infinity);
        return [...withLocation, ...withoutLocation];
    }

    if (mode === URGENCY_MODES.FLEXIBLE) {
        // Providers within ~25 km, sorted by rating (best match)
        const nearby = filterProvidersByRadius(providers, userLat, userLng, 25);
        nearby.sort((a, b) => (b.rating || 0) - (a.rating || 0));

        // If no one is within 25 km, expand to 50 km
        if (nearby.length === 0) {
            const expanded = filterProvidersByRadius(providers, userLat, userLng, 50);
            expanded.sort((a, b) => (b.rating || 0) - (a.rating || 0));
            return expanded;
        }

        return nearby;
    }

    // Fallback: return as-is
    return providers;
}
